import { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  Slider,
  FormHelperText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  ExpandMore as ExpandIcon,
  QuestionAnswer as QueryIcon,
  Summarize as SummaryIcon,
  Quiz as FAQIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { SimpleDataService } from '../services/simpleDataService';
import { SimpleDataSession } from '../models/simpleData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

export const SimpleDataAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [activeTab, setActiveTab] = useState(0);
  const [sessions, setSessions] = useState<SimpleDataSession[]>([]);
  const [currentSession, setCurrentSession] = useState<SimpleDataSession | null>(null);
  const [data, setData] = useState('');
  const [query, setQuery] = useState('');
  const [summaryType, setSummaryType] = useState<'brief' | 'detailed' | 'bullet_points'>('brief');
  const [faqCount, setFaqCount] = useState(5);
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fileContent, setFileContent] = useState('');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = () => {
    const allSessions = SimpleDataService.getAllSessions();
    setSessions(allSessions);
  };

  const handleNewSession = () => {
    if (!data.trim()) {
      setError('Please enter some data to analyze');
      return;
    }

    const session = SimpleDataService.createSession(data);
    setCurrentSession(session);
    loadSessions();
    setError(null);
  };

  const handleQuery = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await SimpleDataService.query({
        data: currentSession.data,
        query,
        temperature,
        maxTokens,
      });

      SimpleDataService.addQuery(currentSession.id, query, response.result);
      setCurrentSession(SimpleDataService.getSession(currentSession.id));
      setQuery('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process query');
    } finally {
      setLoading(false);
    }
  };

  const handleSummarize = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await SimpleDataService.summarize({
        data: currentSession.data,
        summaryType,
        temperature,
        maxTokens,
      });

      SimpleDataService.addSummary(currentSession.id, summaryType, response.result);
      setCurrentSession(SimpleDataService.getSession(currentSession.id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFAQ = async () => {
    if (!currentSession) {
      setError('Please start a session first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await SimpleDataService.generateFAQ({
        data: currentSession.data,
        numberOfQuestions: faqCount,
        temperature,
        maxTokens,
      });

      SimpleDataService.addFAQs(currentSession.id, response);
      setCurrentSession(SimpleDataService.getSession(currentSession.id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadSession = (session: SimpleDataSession) => {
    setCurrentSession(session);
    setData(session.data);
    setHistoryOpen(false);
  };

  const handleDeleteSession = (sessionId: string) => {
    SimpleDataService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
      setData('');
    }
    loadSessions();
  };

  const handleExport = () => {
    if (!currentSession) return;

    const content = SimpleDataService.exportSession(currentSession);
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `simple-data-analysis-${currentSession.id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setFileContent(content);
      };
      reader.readAsText(file);
    }
  };

  const handleImportData = () => {
    setData(fileContent);
    setUploadDialogOpen(false);
    setFileContent('');
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
          >
            {isMobile ? 'Data Agent' : 'Simple Data Agent'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={isMobile ? "" : "Session History"}>
              <IconButton 
                onClick={() => setHistoryOpen(true)}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            {currentSession && (
              <Tooltip title={isMobile ? "" : "Export Session"}>
                <IconButton 
                  onClick={handleExport}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                Data Input
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={!isMobile && <UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  fullWidth
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44
                  }}
                >
                  {isMobile ? 'Upload' : 'Upload File'}
                </Button>
              </Box>

              <TextField
                multiline
                rows={isMobile ? 6 : 10}
                fullWidth
                label={isMobile ? "Data" : "Enter your data"}
                value={data}
                onChange={(e) => setData(e.target.value)}
                placeholder={isMobile ? "Paste your data here..." : "Paste your CSV data, JSON, text, or any structured data here..."}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />

              <Button
                variant="contained"
                fullWidth
                onClick={handleNewSession}
                disabled={!data.trim() || loading}
                startIcon={!isMobile && <PlayIcon />}
                sx={{ 
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: 44
                }}
              >
                {isMobile ? 'Start Session' : 'Start New Session'}
              </Button>

              {currentSession && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mt: 2,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  {isMobile ? `ID: ${currentSession.id.slice(0, 8)}...` : `Session: ${currentSession.id}`}
                </Alert>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Tabs 
                value={activeTab} 
                onChange={(_, val) => setActiveTab(val)} 
                sx={{ mb: 2 }}
                variant={isMobile ? "fullWidth" : "standard"}
                orientation={isMobile ? "horizontal" : "horizontal"}
              >
                <Tab 
                  label="Query" 
                  icon={<QueryIcon />}
                  iconPosition="start"
                  sx={{ 
                    minHeight: 48,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    '& .MuiTab-iconWrapper': {
                      display: { xs: 'none', md: 'inline-flex' }
                    }
                  }}
                />
                <Tab 
                  label={isMobile ? "Summary" : "Summarize"} 
                  icon={<SummaryIcon />}
                  iconPosition="start"
                  sx={{ 
                    minHeight: 48,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    '& .MuiTab-iconWrapper': {
                      display: { xs: 'none', md: 'inline-flex' }
                    }
                  }}
                />
                <Tab 
                  label="FAQ" 
                  icon={<FAQIcon />}
                  iconPosition="start"
                  sx={{ 
                    minHeight: 48,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    '& .MuiTab-iconWrapper': {
                      display: { xs: 'none', md: 'inline-flex' }
                    }
                  }}
                />
                <Tab 
                  label="Settings" 
                  icon={<HelpIcon />}
                  iconPosition="start"
                  sx={{ 
                    minHeight: 48,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    '& .MuiTab-iconWrapper': {
                      display: { xs: 'none', md: 'inline-flex' }
                    }
                  }}
                />
              </Tabs>

              <TabPanel value={activeTab} index={0}>
                <Box>
                  <TextField
                    fullWidth
                    label={isMobile ? "Question" : "Ask a question about your data"}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={isMobile ? "What are the trends?" : "e.g., What are the main trends? What's the average value?"}
                    sx={{ mb: 2 }}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handleQuery}
                    disabled={!currentSession || !query.trim() || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44
                    }}
                  >
                    {loading ? 'Processing...' : (isMobile ? 'Query' : 'Run Query')}
                  </Button>

                  {currentSession?.queries && currentSession.queries.length > 0 && (
                    <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'History' : 'Query History'}
                      </Typography>
                      {currentSession.queries.map((q, index) => (
                        <Accordion key={index}>
                          <AccordionSummary expandIcon={<ExpandIcon />}>
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {q.query}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {q.result}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                mt: 1, 
                                display: 'block',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {new Date(q.timestamp).toLocaleString()}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  )}
                </Box>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <Box>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {isMobile ? 'Type' : 'Summary Type'}
                    </InputLabel>
                    <Select
                      value={summaryType}
                      onChange={(e) => setSummaryType(e.target.value as any)}
                      label={isMobile ? 'Type' : 'Summary Type'}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      <MenuItem value="brief" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Brief Summary
                      </MenuItem>
                      <MenuItem value="detailed" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Detailed Summary
                      </MenuItem>
                      <MenuItem value="bullet_points" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Bullet Points
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    onClick={handleSummarize}
                    disabled={!currentSession || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <SummaryIcon />)}
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44
                    }}
                  >
                    {loading ? 'Generating...' : (isMobile ? 'Summarize' : 'Generate Summary')}
                  </Button>

                  {currentSession?.summaries && currentSession.summaries.length > 0 && (
                    <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'History' : 'Summary History'}
                      </Typography>
                      {currentSession.summaries.map((s, index) => (
                        <Card key={index} sx={{ mb: 2 }}>
                          <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              color="primary" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              {s.type.replace('_', ' ').toUpperCase()}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontSize: { xs: '0.875rem', sm: '0.95rem' }
                              }}
                            >
                              {s.result}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                mt: 1, 
                                display: 'block',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {new Date(s.timestamp).toLocaleString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <Box>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography 
                      gutterBottom
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '0.95rem' },
                        fontWeight: 600
                      }}
                    >
                      {isMobile ? 'Questions' : 'Number of Questions'}: {faqCount}
                    </Typography>
                    <Slider
                      value={faqCount}
                      onChange={(_, val) => setFaqCount(val as number)}
                      min={3}
                      max={20}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {isMobile ? 'FAQ count' : 'How many FAQ questions to generate'}
                    </FormHelperText>
                  </FormControl>

                  <Button
                    variant="contained"
                    onClick={handleGenerateFAQ}
                    disabled={!currentSession || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <FAQIcon />)}
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44
                    }}
                  >
                    {loading ? 'Generating...' : (isMobile ? 'Generate' : 'Generate FAQs')}
                  </Button>

                  {currentSession?.faqs && currentSession.faqs.length > 0 && (
                    <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                      >
                        {isMobile ? 'FAQs' : 'Generated FAQs'}
                      </Typography>
                      {currentSession.faqs.map((faqSet, setIndex) => (
                        <Card key={setIndex} sx={{ mb: 2 }}>
                          <CardContent sx={{ p: { xs: 2, sm: 2 } }}>
                            {faqSet.faqs.map((faq, index) => (
                              <Box key={index} sx={{ mb: 2 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  color="primary"
                                  sx={{ fontSize: { xs: '0.875rem', sm: '0.95rem' } }}
                                >
                                  Q{index + 1}: {faq.question}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    mt: 1,
                                    fontSize: { xs: '0.875rem', sm: '0.95rem' }
                                  }}
                                >
                                  {faq.answer}
                                </Typography>
                                {index < faqSet.faqs.length - 1 && <Divider sx={{ my: 2 }} />}
                              </Box>
                            ))}
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                mt: 1, 
                                display: 'block',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              {isMobile ? 'Gen: ' : 'Generated: '}{new Date(faqSet.timestamp).toLocaleString()}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    {isMobile ? 'Settings' : 'Model Settings'}
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <Typography 
                      gutterBottom
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '0.95rem' },
                        fontWeight: 600
                      }}
                    >
                      Temperature: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, val) => setTemperature(val as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {isMobile ? '0=focused, 1=creative' : 'Controls randomness: 0 = focused, 1 = creative'}
                    </FormHelperText>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <Typography 
                      gutterBottom
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '0.95rem' },
                        fontWeight: 600
                      }}
                    >
                      Max Tokens: {maxTokens}
                    </Typography>
                    <Slider
                      value={maxTokens}
                      onChange={(_, val) => setMaxTokens(val as number)}
                      min={256}
                      max={4096}
                      step={256}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {isMobile ? 'Max response length' : 'Maximum length of the response'}
                    </FormHelperText>
                  </FormControl>

                  <Alert 
                    severity="info"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {isMobile 
                      ? 'Settings apply to all operations' 
                      : 'These settings apply to all operations (query, summarize, and FAQ generation).'
                    }
                  </Alert>
                </Box>
              </TabPanel>
            </Paper>
          </Grid>
        </Grid>

        {/* Session History Dialog */}
        <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            {isMobile ? 'History' : 'Session History'}
          </DialogTitle>
          <DialogContent>
            <List>
              {sessions.map((session) => (
                <Fragment key={session.id}>
                  <ListItem sx={{ py: { xs: 1.5, sm: 2 } }}>
                    <ListItemText
                      primary={isMobile ? session.id.slice(0, 12) + '...' : session.id}
                      secondary={
                        isMobile 
                          ? `${new Date(session.createdAt).toLocaleDateString()}`
                          : `Created: ${new Date(session.createdAt).toLocaleString()} | ${session.queries.length} queries, ${session.summaries.length} summaries, ${session.faqs.length} FAQ sets`
                      }
                      primaryTypographyProps={{
                        sx: { fontSize: { xs: '0.9rem', sm: '1rem' } }
                      }}
                      secondaryTypographyProps={{
                        sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={isMobile ? "" : "Load Session"}>
                        <IconButton 
                          onClick={() => handleLoadSession(session)}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isMobile ? "" : "Delete Session"}>
                        <IconButton 
                          onClick={() => handleDeleteSession(session.id)}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </Fragment>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setHistoryOpen(false)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
            {isMobile ? 'Upload File' : 'Upload Data File'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                accept=".txt,.csv,.json,.xml"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button 
                  variant="outlined" 
                  component="span" 
                  fullWidth
                  sx={{ 
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44
                  }}
                >
                  Choose File
                </Button>
              </label>
              
              {fileContent && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    multiline
                    rows={isMobile ? 6 : 8}
                    fullWidth
                    value={fileContent}
                    onChange={(e) => setFileContent(e.target.value)}
                    label={isMobile ? "Content" : "File Content (you can edit)"}
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setUploadDialogOpen(false)}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleImportData} 
              disabled={!fileContent}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44
              }}
            >
              Import
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};